import type { DriveStep } from 'driver.js';
import { createLogger } from '../../utils/logger';
import { SIDEBAR_DRAWER_QUERY } from '../../hooks/useIsMobile';

const logger = createLogger('tourMobileSidebar');

interface MobileSidebarControls {
  open: () => void;
  close: () => void;
}

// Layout.tsx owns the actual mobile-drawer React state; it registers itself
// here on mount so tour steps (which live outside the layout tree) can open
// and close the drawer without a direct import cycle between
// components/tutorial and components/layout.
let controls: MobileSidebarControls | null = null;

export function registerMobileSidebarControls(next: MobileSidebarControls | null): void {
  controls = next;
}

// Must be the drawer's own breakpoint, not a phone-width guess: between the two the
// sidebar is off-canvas at translateX(-100%) but the tour thinks it is on-screen, so
// driver.js highlights nav items nobody can see.
function isSidebarDrawerViewport(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.(SIDEBAR_DRAWER_QUERY)?.matches;
}

export function openMobileSidebarForTour(): void {
  if (!controls) {
    logger.warn('openMobileSidebarForTour called before Layout registered controls');
    return;
  }
  controls.open();
}

export function closeMobileSidebarForTour(): void {
  if (!controls) return;
  controls.close();
}

// Consecutive sidebar-nav steps fire this step's onDeselected immediately
// before the next step's onHighlightStarted — closing synchronously would
// flash the drawer shut and back open between them. Defer the close by one
// tick and let a subsequent open cancel it.
let pendingCloseId: ReturnType<typeof setTimeout> | null = null;

function cancelPendingClose(): void {
  if (pendingCloseId !== null) {
    clearTimeout(pendingCloseId);
    pendingCloseId = null;
  }
}

/**
 * Wraps a reference-tour step targeting a `.sidebar`-scoped element
 * (e.g. `#tour-nav-*`) so the off-canvas drawer opens right before it's
 * highlighted and closes again once the tour moves past it — only at the
 * widths where the sidebar actually is a drawer (SIDEBAR_DRAWER_QUERY).
 * Chains through any onHighlightStarted/onDeselected already on the step
 * rather than overwriting them.
 */
export function withMobileSidebarOpen(step: DriveStep): DriveStep {
  const prevOnHighlightStarted = step.onHighlightStarted;
  const prevOnDeselected = step.onDeselected;

  return {
    ...step,
    onHighlightStarted: (element, stepArg, opts) => {
      if (isSidebarDrawerViewport()) {
        cancelPendingClose();
        openMobileSidebarForTour();
      }
      prevOnHighlightStarted?.(element, stepArg, opts);
    },
    onDeselected: (element, stepArg, opts) => {
      if (isSidebarDrawerViewport()) {
        cancelPendingClose();
        pendingCloseId = setTimeout(() => {
          pendingCloseId = null;
          closeMobileSidebarForTour();
        }, 0);
      }
      prevOnDeselected?.(element, stepArg, opts);
    },
  };
}
